// ERP-5128 — Final Result Rule for Pricing Templates.
//
// A template's fees produce an exact, fractional platform price. The Final
// Result Rule decides how that raw number is presented as the Platform Price,
// and applies everywhere a template is evaluated: the Test Calculation modal on
// Add/Edit Pricing Template, and Platform Pricing's "Use Pricing Template".
//
// Slugs match what the API stores (1 = Rounding Only, 2 = Rounding and Ending
// in 8); ROUNDING_ONLY is the default for templates saved before this field
// existed, so a missing/unknown value falls back to it.
export const ROUNDING_ONLY = 'rounding_only';
export const ROUNDING_ENDING_IN_8 = 'rounding_ending_in_8';

// `description` is the tooltip copy for each rule — see FinalResultRuleTooltip.
export const FINAL_RESULT_RULES = [
    {
        value: ROUNDING_ONLY,
        label: 'Rounding Only',
        description: 'Rounding to the nearest integer.',
    },
    {
        value: ROUNDING_ENDING_IN_8,
        label: 'Rounding and Ending in 8',
        description: 'Rounding to the nearest integer, then rounding up to a multiple of 10 and minus 2.',
    },
];

export const normalizeFinalResultRule = rule =>
    rule === ROUNDING_ENDING_IN_8 ? ROUNDING_ENDING_IN_8 : ROUNDING_ONLY;

export const finalResultRuleLabel = rule =>
    (FINAL_RESULT_RULES.find(r => r.value === normalizeFinalResultRule(rule)) || FINAL_RESULT_RULES[0]).label;

// Applies the rule to a raw calculated price and returns a whole number.
//
//   Rounding Only          1234.5678 -> 1235
//   Rounding and Ending in 8   1234.5678 -> 1235 -> 1240 -> 1238
//
// The multiple-of-10 step uses modulo rather than Math.ceil(n / 10) * 10 so the
// arithmetic stays exact on integers — a value already ending in 0 must stay
// put (2290 -> 2290 -> 2288), which a float division could nudge upward.
//
// Negative results are clamped to 0: "minus 2" would otherwise turn a 0 price
// into -2, and no consumer here ever prices below zero.
export const applyFinalResultRule = (amount, rule) => {
    const num = Number(amount);
    if (!isFinite(num)) return 0;

    const rounded = Math.round(num);
    if (normalizeFinalResultRule(rule) === ROUNDING_ONLY) return Math.max(0, rounded);

    const remainder = rounded % 10;
    const upToMultipleOf10 = remainder === 0 ? rounded : rounded + (10 - remainder);
    return Math.max(0, upToMultipleOf10 - 2);
};
